'use client';

import { type CSSProperties, useCallback, useState } from 'react';
import Image, { StaticImageData } from 'next/image';

export type StaticMediaImageProps = {
  src: StaticImageData;
  alt: string;
  priority?: boolean;
  quality?: number;
  className?: string;
  style?: CSSProperties;
};

function isValidStaticImage(src: unknown): src is StaticImageData {
  return (
    typeof src === 'object' &&
    src !== null &&
    'src' in src &&
    typeof (src as StaticImageData).src === 'string' &&
    (src as StaticImageData).src.length > 0
  );
}

/**
 * Bundled static imports with Chakra `as={Image}` can break Next.js image wiring and yield /_next/image 404s.
 * Always bypasses the optimizer for local static assets and hides the image if loading fails.
 */
export const StaticMediaImage = ({
  src,
  alt,
  priority,
  quality = 100,
  className,
  style
}: StaticMediaImageProps) => {
  const [failed, setFailed] = useState(() => !isValidStaticImage(src));
  const onError = useCallback(() => setFailed(true), []);

  if (failed) return null;

  return (
    <Image
      src={src}
      alt={alt}
      priority={priority}
      quality={quality}
      unoptimized
      onError={onError}
      className={className}
      style={style}
    />
  );
};
